/**
 * Cameras API Service
 * 
 * Provides access to camera management endpoints for Phase C-Lite
 */

import { api } from './api';

/**
 * User-visible message for DELETE /registry/devices/.../cameras/... failures
 * (e.g. 409 camera_in_use, FK violations with hint).
 */
export function formatRegistryCameraDeleteError(err: unknown): string {
  const e = err as {
    message?: string;
    response?: { data?: Record<string, unknown>; status?: number };
  };
  const d = e?.response?.data;
  if (!d || typeof d !== 'object') {
    return e?.message || 'Failed to remove camera';
  }
  const hint = typeof d.hint === 'string' ? d.hint.trim() : '';
  if (hint) return hint;
  const detail = typeof d.detail === 'string' ? d.detail.trim() : '';
  if (detail && detail.length < 800) return detail;
  const code = d.error;
  if (code === 'camera_not_found') {
    return 'Camera was not found (it may have already been removed).';
  }
  if (code === 'camera_in_use') {
    return 'This camera is still referenced elsewhere (for example a policy). Remove or reassign that reference, then try again.';
  }
  if (typeof code === 'string' && code.length > 0) {
    return String(code).replace(/_/g, ' ');
  }
  return 'Failed to remove camera';
}

export interface Camera {
  camera_id: string;
  edge_device_id: string;
  name: string;
  profile_id?: 'A' | 'B' | 'C' | 'D';
  profile_name?: string;
  slug?: string;
  rtsp_url?: string;
  pipeline?: string;
  pipeline_override?: string | null;
  status: 'running' | 'ready' | 'deploying' | 'failed' | 'offline' | 'unknown' | 'disabled';
  enabled?: boolean;
  enabled_meta?: { changed_at?: string; changed_by?: string; reason?: string };
  health_state?: {
    state?: 'READY' | 'DEGRADED' | 'OFFLINE' | 'AUTH_FAILED' | 'STREAM_ERROR';
    reason?: string;
    last_check_at?: string;
    device_last_seen?: string | null;
    age_s?: number | null;
    last_frame_at?: string;
    fix?: { kind?: string; href?: string };
    [key: string]: any;
  };
  health?: {
    status?: string;
    last_frame?: string;
    last_error?: string;
    [key: string]: any;
  };
  created_at?: string;
  updated_at?: string;
}

export interface CameraStatus {
  camera_id: string;
  status: 'running' | 'ready' | 'deploying' | 'failed' | 'offline' | 'unknown' | 'disabled';
  last_frame?: string;
  last_error?: string;
  pipeline_running?: boolean;
  edge_device_id?: string;
  health_state?: Camera['health_state'];
}

export interface CameraTestResult {
  ok: boolean;
  org_id?: string;
  site_id?: string;
  edge_device_id?: string;
  rtsp_url?: string;
  reachable?: boolean;
  auth_ok?: boolean;
  codec?: string;
  resolution?: string;
  fps?: number;
  latency_ms?: number;
  warnings?: string[];
  edge_result?: any;
  error?: string;
  detail?: string;
}

export interface CameraCreateData {
  name: string;
  rtsp_url: string;
  edge_device_id: string;
  pipeline?: string;
  profile_id?: 'A' | 'B' | 'C' | 'D';
  rtsp_username?: string;
  rtsp_password?: string;
}

export interface Epic46CameraProfile {
  profile_id: 'A' | 'B' | 'C' | 'D';
  profile_name: string;
  role?: string | null;
}

export interface CameraListResponse {
  ok: boolean;
  org_id?: string;
  site_id?: string;
  items: Camera[];
  count: number;
  summary?: {
    camera_count: number;
    enabled_count: number;
    device_count: number;
    online_device_count: number;
    cameras_on_online_devices?: number;
    cameras_on_offline_devices?: number;
    cameras_runtime_ready?: number;
    cameras_live_view_ready?: number;
    cameras_waiting?: number;
    includes_edge_runtime: boolean;
  };
}

export interface LiveViewReadiness {
  ok: boolean;
  org_id?: string;
  site_id?: string;
  online_device_count: number;
  cameras_on_online_devices: number;
  cameras_on_offline_devices: number;
  cameras_runtime_ready: number;
  cameras_live_view_ready: number;
  cameras_waiting: number;
  devices: Array<{
    device_id: string;
    online: boolean;
    callback_host_set: boolean;
    cameras_enabled: number;
    cameras_live_view_ready: number;
    cameras_waiting: number;
    live_view_ok: boolean;
  }>;
}

// API Functions
export const camerasApi = {
  /**
   * List cameras for a site
   */
  listCameras: async (
    siteId: string,
    deviceId?: string,
    opts?: { includeEdgeRuntime?: boolean; scheduleAutoPreflight?: boolean },
  ): Promise<CameraListResponse> => {
    const params: Record<string, any> = {};
    if (deviceId) params.device_id = deviceId;
    if (opts?.includeEdgeRuntime !== undefined) {
      params.include_edge_runtime = opts.includeEdgeRuntime ? 'true' : 'false';
    }
    if (opts?.scheduleAutoPreflight !== undefined) {
      params.schedule_auto_preflight = opts.scheduleAutoPreflight ? 'true' : 'false';
    }
    const response = await api.get(`/api/v1/sites/${siteId}/cameras`, { params });
    return response.data;
  },

  /**
   * Thin bootstrap list for fast first paint on camera-centric pages.
   */
  listCameraBootstrap: async (siteId: string, deviceId?: string): Promise<CameraListResponse> => {
    const params: Record<string, any> = {};
    if (deviceId) params.device_id = deviceId;
    const response = await api.get(`/api/v1/sites/${siteId}/cameras/bootstrap`, {
      params,
      timeout: 90000,
    });
    return response.data;
  },

  getLiveViewReadiness: async (siteId: string, deviceId?: string): Promise<LiveViewReadiness> => {
    const params: Record<string, any> = {};
    if (deviceId) params.device_id = deviceId;
    const response = await api.get(`/api/v1/sites/${siteId}/live_view/readiness`, { params });
    return response.data;
  },

  /**
   * Test RTSP camera connection
   */
  testCamera: async (
    siteId: string,
    rtspUrl: string,
    edgeDeviceId?: string
  ): Promise<CameraTestResult> => {
    const response = await api.post(`/api/v1/sites/${siteId}/cameras/test`, {
      rtsp_url: rtspUrl,
      edge_device_id: edgeDeviceId,
    });
    return response.data;
  },

  /**
   * Add a new camera
   */
  addCamera: async (siteId: string, data: CameraCreateData): Promise<Camera> => {
    const response = await api.post(`/api/v1/sites/${siteId}/cameras`, data);
    return response.data;
  },

  /**
   * Delete camera
   */
  deleteCamera: async (edgeDeviceId: string, cameraId: string) => {
    const response = await api.delete(`/api/v1/registry/devices/${edgeDeviceId}/cameras/${cameraId}`);
    return response.data;
  },

  /**
   * Get camera status with 429 rate limit handling
   */
  getCameraStatus: async (cameraId: string, edgeDeviceId: string): Promise<CameraStatus> => {
    try {
      const response = await api.get(`/api/v1/cameras/${cameraId}/status`, {
        params: { edge_device_id: edgeDeviceId },
        timeout: 5000,
      });
      return response.data;
    } catch (err: any) {
      if (err.response?.status === 429) {
        const retryAfter = err.response.headers?.['retry-after'] || '60';
        const waitSeconds = parseInt(retryAfter, 10) || 60;
        const error = new Error(`Rate limited. Retry after ${waitSeconds}s`) as any;
        error.retryAfterSeconds = waitSeconds;
        error.status = 429;
        throw error;
      }
      throw err;
    }
  },

  /**
   * Get camera snapshot
   */
  getCameraSnapshot: async (cameraId: string, edgeDeviceId: string): Promise<string> => {
    const response = await api.get(`/api/v1/cameras/${cameraId}/snapshot`, {
      params: { edge_device_id: edgeDeviceId },
      responseType: 'blob',
    });
    // Convert blob to data URL
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(response.data);
    });
  },

  /**
   * Get camera thumbnail (cached server-side)
   */
  getCameraThumbnail: async (cameraId: string, edgeDeviceId: string): Promise<string> => {
    const response = await api.get(`/api/v1/cameras/${cameraId}/thumbnail`, {
      params: { edge_device_id: edgeDeviceId },
      responseType: 'blob',
    });
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(response.data);
    });
  },

  /**
   * Enable/disable camera
   */
  setCameraEnabled: async (siteId: string, cameraId: string, edgeDeviceId: string, enabled: boolean, reason?: string) => {
    const response = await api.patch(`/api/v1/sites/${siteId}/cameras/${cameraId}/enabled`, {
      edge_device_id: edgeDeviceId,
      enabled,
      reason,
    });
    return response.data;
  },

  /**
   * Set per-camera pipeline override (e.g., switch camera to OCR offering via pipeline="ocr_stream").
   */
  setCameraPipeline: async (siteId: string, cameraId: string, edgeDeviceId: string, pipeline: string | null) => {
    const response = await api.post(`/api/v1/sites/${siteId}/cameras/${cameraId}/pipeline`, {
      edge_device_id: edgeDeviceId,
      pipeline,
    });
    return response.data;
  },

  getEpic46CameraProfiles: async (): Promise<Epic46CameraProfile[]> => {
    const response = await api.get('/api/v1/epic46/camera-profiles');
    const profiles = response?.data?.catalog?.profiles;
    if (!Array.isArray(profiles)) return [];
    return profiles
      .map((p: any) => ({
        profile_id: String(p?.profile_id || '').toUpperCase(),
        profile_name: String(p?.profile_name || ''),
        role: p?.role ?? null,
      }))
      .filter((p: any) => ['A', 'B', 'C', 'D'].includes(p.profile_id) && p.profile_name) as Epic46CameraProfile[];
  },

  setCameraProfile: async (
    siteId: string,
    cameraId: string,
    edgeDeviceId: string,
    profileId: 'A' | 'B' | 'C' | 'D'
  ) => {
    const response = await api.post(`/api/v1/sites/${siteId}/cameras/${cameraId}/profile`, {
      edge_device_id: edgeDeviceId,
      profile_id: profileId,
    });
    return response.data;
  },
};

export default camerasApi;


